<template>
  <section class="ren-decoration-cp ren-decoration-cp-record-list">
    <header v-if="title || moreText">
      <div>
        <strong v-if="title">{{ title }}</strong>
        <span v-if="subtitle">{{ subtitle }}</span>
      </div>
      <em v-if="moreText">{{ moreText }}</em>
    </header>
    <p v-if="items.length === 0" class="ren-decoration-cp-record-list__empty">{{ emptyText }}</p>
    <article v-for="(item, index) in items" v-else :key="itemKey(item, index)">
      <i :style="{ backgroundColor: iconColor }">{{ itemIcon(item) }}</i>
      <div>
        <strong>{{ itemTitle(item) }}</strong>
        <span v-if="itemSubtitle(item)">{{ itemSubtitle(item) }}</span>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  numberValue,
  previewBoundString,
  previewRecordsFor,
  recordList,
  stringValue
} from '../previews/preview-helpers'
import type { WidgetPreviewProps } from './types'

const props = defineProps<
  WidgetPreviewProps & {
    emptyLabel?: string
  }
>()

const title = computed(() => stringValue(props.component.props?.title, props.component.title || ''))
const subtitle = computed(() => stringValue(props.component.props?.subtitle))
const moreText = computed(() => stringValue(props.component.props?.moreText))
const emptyText = computed(() => stringValue(props.component.props?.emptyText, `${props.emptyLabel || title.value || '列表'}暂无数据`))
const iconColor = computed(
  () => props.themeTokens?.primaryColor || props.themeColor || 'var(--ren-theme-primary)'
)
const items = computed(() => {
  const records = previewRecordsFor(
    props.component,
    props.componentData,
    recordList(props.component.props?.items ?? props.component.props?.entries ?? props.component.props?.tabs)
  )
  const limit = numberValue(props.component.props?.limit ?? props.component.props?.pageSize, records.length || 8)
  const size = Number.isFinite(limit) && limit > 0 ? Math.min(20, Math.round(limit)) : records.length
  return records.slice(0, size)
})

function itemTitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.title ?? item.name ?? item.label, '条目'),
    item
  )
}

function itemSubtitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.subtitle',
    stringValue(item.subtitle ?? item.summary ?? item.description ?? item.source),
    item
  )
}

function itemIcon(item: Record<string, unknown>) {
  const value = stringValue(item.icon ?? item.badge).trim()
  return (value || itemTitle(item)).slice(0, 1)
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code ?? item.value, `${itemTitle(item)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-record-list {
  display: grid;
  gap: 8px;
  background: transparent;
}

.ren-decoration-cp-record-list header strong,
.ren-decoration-cp-record-list article strong {
  color: var(--ren-theme-text);
}

.ren-decoration-cp-record-list header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ren-decoration-cp-record-list header div {
  display: grid;
  gap: 4px;
}

.ren-decoration-cp-record-list header span,
.ren-decoration-cp-record-list header em,
.ren-decoration-cp-record-list article span,
.ren-decoration-cp-record-list__empty {
  color: var(--ren-theme-muted);
  font-size: 12px;
  font-style: normal;
}

.ren-decoration-cp-record-list article {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ren-decoration-cp-record-list article i {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 9999px;
  color: #fff;
  font-style: normal;
  font-size: 12px;
}

.ren-decoration-cp-record-list article div {
  min-width: 0;
  display: grid;
  gap: 2px;
}

.ren-decoration-cp-record-list__empty {
  min-height: 72px;
  display: grid;
  place-items: center;
  margin: 0;
  background: var(--ren-theme-background);
}
</style>
